import { StatusBadge } from "@components/display/StatusBadge";
import { useEtatDemande } from "@lib/hooks/collections/useEtatDemande";
import { STATUS } from "../config/statuses";

export interface EtatDemandeBadgeProps {
  /** EtatDemande backend code (e.g. 'SOUMISE', 'APPROUVEE') */
  code?: string | null;
  /** Display label (optional — fetched from API if not provided) */
  libelle?: string;
  /** Badge size: 'sm' | 'md' | 'lg' (default: 'md') */
  size?: "sm" | "md" | "lg";
  /** Whether to add a pulse animation (useful for urgent states) */
  pulse?: boolean;
}

/**
 * Domain wrapper (plan §4) — keeps the legacy public API ({code, libelle, size,
 * pulse}) and delegates to the shared StatusBadge with the conges STATUS config.
 *
 * Fixes the latent rendering bug of the legacy .jsx: it emitted a legacy status
 * badge class that was never defined in badge.css, so the badge rendered with
 * no status colors at all. StatusBadge resolves token → CSS vars, so every
 * code now renders a real badge.
 */
function EtatDemandeBadge({ code, libelle, size = "md", pulse = false }: EtatDemandeBadgeProps) {
  // Preserve legacy behavior: fetch the authoritative label from the API when the
  // caller did not provide one (per-client fixtures can override EtatDemande labels).
  const { data: etatDemandeData } = useEtatDemande(code);

  const displayLibelle = libelle ?? etatDemandeData?.libelle ?? undefined;
  // StatusBadge supports sm/md only; keep the legacy lg slot for API compat.
  const badgeSize = size === "lg" ? "md" : size;

  return (
    <StatusBadge
      status={code ?? ""}
      config={STATUS}
      label={displayLibelle}
      size={badgeSize}
      className={pulse ? "animate-pulse" : undefined}
    />
  );
}

export { EtatDemandeBadge };
export default EtatDemandeBadge;
